import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule, Http, RequestOptions, XHRBackend } from '@angular/http';
import { Location } from '@angular/common';
import { NgModule, ApplicationRef } from '@angular/core';
import { removeNgStyles, createNewHosts, createInputTransfer } from '@angularclass/hmr';
import { Router, RouterModule, PreloadAllModules } from '@angular/router';

/*
 * Third party libs
 */
// import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'toastr-ng2';
import { TranslateModule, TranslateService, TranslatePipe, TranslateLoader, TranslateStaticLoader } from 'ng2-translate';
import { MyDatePickerModule } from 'mydatepicker';


/*
 * Platform and Environment providers/directives/pipes
 */
import { AppRoutes } from './app.routes';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { ENV_PROVIDERS } from './environment';
import { GlobalState } from './global.state';
import { Settings } from './settings';

// App is our top level component
import { AppComponent } from './app.component';
import { AppState, InternalStateType } from './app.service';
import { NoContentComponent } from './no-content';
import { LoginComponent } from './pages/login';

/*
 * Application services
 */
import { HttpClient } from './services/http/http-client.service';
import { MemberUserService } from './services/http/member-user.service';
import { MemberUserPermissionService } from './services/http/member-user-permission.service';
import { ClubGoalService } from './services/http/club-goal.service';
import { ClubService } from './services/http/club.service';
import { FiscalYearService } from './services/http/fiscal-year.service';
import { GoalCategoryService } from './services/http/goal-category.service';
import { LanguageService } from './services/http/language.service';
import { GoalService } from './services/http/goal.service';
import { VersionService } from './services/http/version.service';
import { OrgNodeService } from './services/http/org-node.service';
import { SecurityService } from './services/security/security.service';
import { RolledUpGoalsByOrgNodeService } from './services/http/rolled-up-goals-by-org-node.service';
import { OrgNodeTypeService } from './services/http/org-node-type.service';
import { TrendDataService } from './services/http/trend-data.service';
import { ProjectCategoryService } from './services/http/project-category.service';
import { ProjectPartnerService } from './services/http/project-partner.service';
import { ProjectAreaOfFocusService } from './services/http/project-area-of-focus.service';
import { ClubProjectService } from './services/http/club-project.service';
import { TranslationKeyService } from './services/http/translation-key.service';
import { TranslationService } from './services/http/translation.service';
import { GoalTypeService } from './services/http/goal-type.service';
import { TranslationCategoryService } from './services/http/translation-category.service';
import { ResourceService } from './services/http/resource.service';
import { ResourceCategoryService } from './services/http/resource-category.service';
import { AuditLogService } from './services/http/audit-log.service';
import { ClubRatingService } from './services/http/club-rating.service';
import { AuthenticationService } from './services/http/authentication.service';
import { ReportService } from './services/http/report.service';
import { ReportCategoryService } from './services/http/report-category.service';
import { ReportSubCategoryService } from './services/http/report-sub-category.service';
import { SecurityGroupService } from './services/http/security-group.service';
import { ReportOrganizationTypeService } from './services/http/report-organization-type.service';
import { TranslationKeyDtoService } from './services/http/translation-key-dto.service';

import { AlertModule, DropdownModule, ModalModule, TypeaheadModule, DatepickerModule, PaginationModule } from 'ng2-bootstrap';
import { MomentModule } from 'angular2-moment';

import { AuthGuard } from './services/security/auth-guard.service';
import { GoalsPopupService } from './services/http/goals-popup.service';

//This works for extending native types, but I'm unsure if this is the right way to do it
require("./extensions/extensions.js");


// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
    AppState,
    AuthGuard,
    GlobalState,
    ClubGoalService,
    ClubService,
    FiscalYearService,
    GoalCategoryService,
    LanguageService,
    MemberUserService,
    GoalService,
    MemberUserPermissionService,
    SecurityService,
    HttpClient,
    VersionService,
    OrgNodeService,
    RolledUpGoalsByOrgNodeService,
    OrgNodeTypeService,
    TrendDataService,
    ProjectCategoryService,
    ProjectPartnerService,
    ProjectAreaOfFocusService,
    ClubProjectService,
    TranslationKeyService,
    TranslationKeyDtoService,
    TranslationService,
    GoalTypeService,
    TranslationCategoryService,
    ResourceService,
    ResourceCategoryService,
    AuditLogService,
    ClubRatingService,
    AuthenticationService,
    ReportService,
    ReportCategoryService,
    ReportSubCategoryService,
    SecurityGroupService,
    ReportOrganizationTypeService,
    GoalsPopupService
];

type StoreType = {
  state: InternalStateType,
  restoreInputValues: () => void,
  disposeOldHosts: () => void
};

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [ AppComponent ],
  declarations: [
    AppComponent,
    NoContentComponent,
    LoginComponent
  ],
  imports: [ // import Angular's modules
    AlertModule.forRoot(),
    DropdownModule.forRoot(),
    ModalModule.forRoot(),
    TypeaheadModule.forRoot(),
    PaginationModule.forRoot(),
    DatepickerModule.forRoot(),
    MyDatePickerModule,
    MomentModule,

    // NgbModule.forRoot(),
    ToastrModule.forRoot(),
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    AppRoutes,
    TranslateModule.forRoot({
      provide: TranslateLoader,
      useFactory: (http: Http) => new TranslateStaticLoader(http, `${Settings.apiUrl}/api/translations`, ""),
      deps: [Http]
    })
  ],
  exports: [
    TranslateModule
  ],
  providers: [ // expose our Services and Providers into Angular's dependency injection
    ENV_PROVIDERS,
    APP_PROVIDERS,
    GlobalState,
    {
      provide: HttpClient,
      useFactory: (backend: XHRBackend, options: RequestOptions, location:Location) => {
        return new HttpClient(backend, options, location);
      },
      deps: [XHRBackend, RequestOptions]
    }
  ]
})
export class AppModule {

  constructor(
    public appRef: ApplicationRef,
    public appState: AppState,
    router: Router
  ) {
    // below is for DEBUG Purposes only
    console.log('Routes: ', JSON.stringify(router.config, undefined, 2));
  }

  public hmrOnInit(store: StoreType) {
    if (!store || !store.state) {
      return;
    }
    console.log('HMR store', JSON.stringify(store, null, 2));
    // set state
    this.appState._state = store.state;
    // set input values
    if ('restoreInputValues' in store) {
      let restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  public hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map((cmp) => cmp.location.nativeElement);
    // save state
    const state = this.appState._state;
    store.state = state;
    // recreate root elements
    store.disposeOldHosts = createNewHosts(cmpLocation);
    // save input values
    store.restoreInputValues  = createInputTransfer();
    // remove styles
    removeNgStyles();
  }

  public hmrAfterDestroy(store: StoreType) {
    // display new elements
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/ng-router-loader?{"loader":"async-system","genDir":"compiled","aot":false}!./src/app/app.module.ts